// GiftItemBrand 인터페이스

import { AccountEntity } from '@shared/api/accountConfig/accountConfig.interface';
import { Image } from '@shared/api/media/media.interface';

export interface CouponDetail {
  validPeriod?: string;
  placeOfExchange?: string;
}

export interface DeliveryDetail {
  shippingInfo?: string;
  shippingFeeInfo?: string;
  /** 배송비 (원). 예: 500 */
  shippingFee?: number;
  /** 이 수량 이상이면 무료배송. 예: 20 */
  freeShippingMinQuantity?: number;
  /** 배송 알림 수신 전화번호 (최대 3개, admin에서 수정) */
  notifyPhoneNumbers?: string[];
}

/**
 * 정산정보 (브랜드/공급사 정산용 — 구매자 노출용 DeliveryDetail과 별개).
 * 관리자 전용으로 관리한다.
 */
export interface SettlementInfo {
  /** 이 공급사의 도매가 정산 주체 (마리/가온). bugoBrand.accountConfig.accountEntity와 동일 어휘 */
  accountEntity?: AccountEntity;
  /** 소량 개당 추가단가 (정산 청구분). 수량이 freeShippingMinQuantity 미만이면 개당 가산. 예: 1000 */
  shippingFee?: number;
  /** 이 수량 이상이면 추가단가 미청구. 예: 20 */
  freeShippingMinQuantity?: number;
  /** 택배비 (건당 flat). 예: 4500 */
  courierFee?: number;
  /** 이 수량 이상이면 택배비 무료. 예: 50 */
  courierFreeMinQuantity?: number;
  /** 정산일 (익월 기준 일자). 예: 15 → 익월 15일 */
  settlementDay?: number;
}

/** 인쇄 텍스트 가로 정렬 */
export enum ThanksPrintTextAlign {
  Left = 'left',
  Center = 'center',
  Right = 'right',
}

/**
 * 감사메시지 인쇄 영역 좌표.
 * 템플릿 이미지 기준 정규화 비율(0~1). (x0,y0)=좌상단, (x1,y1)=우하단.
 */
export interface ThanksPrintBox {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
  /** 박스 내 가로 정렬 (기본 center) */
  align?: ThanksPrintTextAlign;
}

/**
 * 감사메시지 인쇄 템플릿 (브랜드당 복수 — 구매자가 이 중 하나 선택).
 * 읽기는 templateImage가 populated(Image), 쓰기(Create/Update)는 image id(string).
 */
export interface ThanksPrintTemplate<TImage = Image> {
  /** 템플릿 고유 id (신규 생성 시 없음, 백엔드가 부여). 주문 시 templateId로 전송 */
  _id?: string;
  /** 템플릿 구분명 (선택지 라벨로 노출) */
  name?: string;
  /** 인쇄되는 형태를 보여주는 샘플 이미지 */
  templateImage?: TImage;
  /** 감사글이 인쇄될 영역 */
  messageBox?: ThanksPrintBox;
  /** "OOO 배상"(상주명)이 인쇄될 영역 */
  senderBox?: ThanksPrintBox;
  /** 표시 순서 (낮을수록 앞) */
  order?: number;
  /** false면 선택지에서 제외 */
  enabled?: boolean;
}

/**
 * 브랜드별 감사메시지 인쇄 설정.
 * 감사글 기본 문구는 백엔드에 저장하지 않음(프론트가 공통 문구로 관리).
 */
export interface ThanksPrintConfig<TImage = Image> {
  /** 이 브랜드가 "감사메시지 인쇄" 브랜드인지 여부 (false/없으면 관련 UI 전부 숨김) */
  enabled: boolean;
  /** "이런 형태로 인쇄되어 발송됩니다" 안내 문구 */
  guideText?: string;
  /** 인쇄 템플릿 목록 (구매자가 이 중 하나 선택) */
  templates: ThanksPrintTemplate<TImage>[];
}

export interface GiftItemBrand {
  _id: string;
  name: string;
  couponDetail?: CouponDetail;
  deliveryDetail?: DeliveryDetail;
  /** 개별배송 허용 여부 (false면 일괄배송 전용). 상품에 설정이 없으면 이 값을 따름 */
  allowIndividualDelivery?: boolean;
  thanksPrint?: ThanksPrintConfig<Image>;
  /** 정산정보 (관리자 전용) */
  settlementInfo?: SettlementInfo;
  createdAt?: string;
  updatedAt?: string;
}

export type GiftItemBrandCreate = {
  name: string;
  couponDetail?: CouponDetail;
  deliveryDetail?: DeliveryDetail;
  /** 개별배송 허용 여부 (false면 일괄배송 전용) */
  allowIndividualDelivery?: boolean;
  thanksPrint?: ThanksPrintConfig<string>;
  /** 정산정보 (관리자 전용) */
  settlementInfo?: SettlementInfo;
};

export type GiftItemBrandUpdate = Partial<GiftItemBrandCreate>;

export type GiftItemBrandList = GiftItemBrand[];
